import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { useBranch } from '../context/BranchContext';
import { useDashboardPreview } from '../context/DashboardPreviewContext';
import { GroupLabel } from './common';
import { CommandPalette } from './CommandPalette';
import { HelpCenter } from './HelpCenter';
import { BootErrorScreen, BootScreen } from './BootScreen';
import { describeActivity, listRecentActivity, type ActivityEvent } from '../api/activity';
import {
  IconAlertTriangle,
  IconArchive,
  IconBell,
  IconBox,
  IconBuilding,
  IconCalendar,
  IconChartBar,
  IconChevronDown,
  IconClipboardCheck,
  IconClock,
  IconDashboard,
  IconFileText,
  IconHelp,
  IconLayers,
  IconLogOut,
  IconMenu,
  IconRepeat,
  IconSearch,
  IconSettings,
  IconShield,
  IconSparkles,
  IconTrash,
  IconTrendUp,
  IconTruck,
  IconUsers,
  IconWallet,
} from './icons';
import type { PermissionAction, PermissionResource, RoleCode } from '../types/domain';

/**
 * App shell: permission-aware navigation, current-user / tenant / role
 * display, active-branch switcher, and logout — all still driven by the
 * same real fn_get_my_context()/fn_check_permission() results as before. A
 * nav item with a `require` entry is hidden — not just disabled — when
 * `hasPermission()` says no.
 *
 * BOOTSTRAP RACE FIX (2026-08-17): this component previously rendered
 * <Outlet/> as soon as a session existed, even while PermissionContext was
 * still resolving fn_get_my_context()/fn_check_permission() on hard
 * refresh — the dashboard components' own effects then captured
 * hasPermission()==false-while-loading at mount time and showed a false
 * "no permission" message, while the sidebar correctly still said "Memuat
 * izin akses...". Fixed by gating the ENTIRE shell (not just nav
 * visibility) on auth+permission+branch bootstrap completing, showing a
 * branded BootScreen instead. `bootstrappedOnce` ensures this full-screen
 * gate only appears on the FIRST load — a later background permission
 * reload (e.g. a Supabase token refresh re-triggering PermissionContext's
 * effect) never blanks an already-visible app, avoiding the flicker a
 * second bootstrap-screen pass would cause.
 */

interface NavItem {
  to: string;
  label: string;
  icon: (props: { size?: number }) => JSX.Element;
  require?: { resource: PermissionResource; action: PermissionAction };
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  { label: 'Menu Utama', items: [{ to: '/app/dashboard', label: 'Dashboard', icon: IconDashboard }] },
  {
    label: 'Master Data',
    items: [
      { to: '/app/items', label: 'Items', icon: IconBox, require: { resource: 'item', action: 'read' } },
      { to: '/app/suppliers', label: 'Suppliers', icon: IconUsers, require: { resource: 'supplier', action: 'read' } },
    ],
  },
  {
    label: 'Purchasing',
    items: [
      { to: '/app/purchase-requests', label: 'Purchase Requests', icon: IconFileText, require: { resource: 'purchase_request', action: 'read' } },
      { to: '/app/purchase-orders', label: 'Purchase Orders', icon: IconClipboardCheck, require: { resource: 'purchase_order', action: 'read' } },
      { to: '/app/goods-receipts', label: 'Goods Receipts', icon: IconTruck, require: { resource: 'goods_receipt', action: 'read' } },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { to: '/app/stock-ledger', label: 'Stock Ledger', icon: IconLayers, require: { resource: 'stock_ledger', action: 'read' } },
      { to: '/app/stock-balance', label: 'Stock Balance', icon: IconBox, require: { resource: 'stock_balance', action: 'read' } },
      { to: '/app/stock-opname', label: 'Stock Opname', icon: IconClipboardCheck, require: { resource: 'stock_count', action: 'read' } },
      { to: '/app/transfers', label: 'Transfers', icon: IconRepeat, require: { resource: 'transfer', action: 'read' } },
      { to: '/app/waste', label: 'Waste', icon: IconTrash, require: { resource: 'waste', action: 'read' } },
      { to: '/app/expiry', label: 'Expiry / Lot', icon: IconArchive, require: { resource: 'expiry_lot', action: 'read' } },
    ],
  },
  {
    label: 'Produksi',
    items: [
      { to: '/app/menus', label: 'Menu Master', icon: IconFileText, require: { resource: 'menu', action: 'read' } },
      { to: '/app/recipes', label: 'Recipes / BOM', icon: IconLayers, require: { resource: 'recipe', action: 'read' } },
      { to: '/app/production', label: 'Produksi', icon: IconClipboardCheck, require: { resource: 'production_batch', action: 'read' } },
    ],
  },
  {
    label: 'SDM',
    items: [
      { to: '/app/employees', label: 'Karyawan', icon: IconUsers, require: { resource: 'employee', action: 'read' } },
      { to: '/app/shifts', label: 'Shift Master', icon: IconClock, require: { resource: 'shift', action: 'read' } },
      { to: '/app/schedule', label: 'Jadwal Kerja', icon: IconCalendar, require: { resource: 'schedule', action: 'read' } },
      { to: '/app/attendance', label: 'Kehadiran', icon: IconShield, require: { resource: 'attendance_event', action: 'read' } },
      { to: '/app/leave', label: 'Cuti', icon: IconCalendar, require: { resource: 'leave_request', action: 'read' } },
      { to: '/app/shift-swaps', label: 'Tukar Shift', icon: IconRepeat, require: { resource: 'shift_swap', action: 'read' } },
      { to: '/app/disciplinary', label: 'Disiplin', icon: IconAlertTriangle, require: { resource: 'disciplinary_action', action: 'read' } },
    ],
  },
  {
    label: 'Keuangan',
    items: [
      { to: '/app/finance/coa', label: 'Chart of Accounts', icon: IconLayers, require: { resource: 'chart_of_account', action: 'read' } },
      { to: '/app/finance/payroll', label: 'Payroll', icon: IconWallet, require: { resource: 'payroll_period', action: 'read' } },
      { to: '/app/finance/ap', label: 'Accounts Payable', icon: IconFileText, require: { resource: 'ap_invoice', action: 'read' } },
      { to: '/app/finance/journals', label: 'Jurnal', icon: IconClipboardCheck, require: { resource: 'journal_entry', action: 'read' } },
      { to: '/app/finance/reports', label: 'Laporan Keuangan', icon: IconTrendUp, require: { resource: 'journal_entry', action: 'read' } },
    ],
  },
  {
    label: 'Aset & CRM',
    items: [
      { to: '/app/assets', label: 'Aset', icon: IconBuilding, require: { resource: 'asset', action: 'read' } },
      { to: '/app/maintenance', label: 'Perawatan', icon: IconSettings, require: { resource: 'maintenance_order', action: 'read' } },
      { to: '/app/crm', label: 'CRM', icon: IconUsers, require: { resource: 'customer', action: 'read' } },
    ],
  },
  {
    label: 'Command Center',
    items: [
      { to: '/app/command-center', label: 'Command Center', icon: IconChartBar, require: { resource: 'report', action: 'read' } },
      { to: '/app/reports', label: 'Report Center', icon: IconFileText, require: { resource: 'report', action: 'read' } },
      { to: '/app/notifications', label: 'Notifikasi', icon: IconBell, require: { resource: 'notification', action: 'read' } },
      { to: '/app/ai', label: 'AI Analyst', icon: IconSparkles, require: { resource: 'ai', action: 'query' } },
      { to: '/app/migration', label: 'Migration Rehearsal', icon: IconRepeat, require: { resource: 'migration', action: 'read' } },
    ],
  },
];

/** Role-aware nav ORDERING (not access — access is already permission-
 * gated per-item above). Each role's real workspace surfaces its most
 * relevant group first, e.g. Warehouse sees Inventory before Purchasing,
 * Purchasing sees its own group first. Groups a role has no visible
 * items in are simply skipped by the render, same as before. */
const NAV_GROUP_PRIORITY: Partial<Record<RoleCode, string[]>> = {
  // WAREHOUSE holds production_batch.create/post (migration 0039) — Produksi
  // sits right after Inventory, the other stock-affecting group it owns.
  // WAREHOUSE has no FASE9 HR grant at all (migration 0040) — SDM sorts last.
  WAREHOUSE: ['Menu Utama', 'Inventory', 'Produksi', 'Purchasing', 'Master Data', 'SDM'],
  // SUPERVISOR holds leave_request/shift_swap/attendance_event.create
  // (migration 0040) — real operational HR duties, so SDM follows Produksi.
  SUPERVISOR: ['Menu Utama', 'Inventory', 'Produksi', 'SDM', 'Purchasing', 'Master Data'],
  // LEADER holds the same FASE9 create grants as Supervisor.
  LEADER: ['Menu Utama', 'SDM', 'Inventory', 'Purchasing', 'Master Data', 'Produksi'],
  // PURCHASING has no FASE9 HR grant at all — SDM sorts last.
  PURCHASING: ['Menu Utama', 'Purchasing', 'Master Data', 'Inventory', 'Produksi', 'SDM'],
  // AUDITOR's only real read grants in this vertical slice land on Inventory
  // (stock_count/transfer/waste/expiry_lot — migration 0033); everything
  // else is hidden by the permission gate regardless of order, but putting
  // Inventory first matches what Auditor can actually open. Produksi read
  // (migration 0039) follows the same reasoning. AUDITOR holds NO FASE9 HR
  // grant (confirmed live, migration 0040 deliberately did not expand
  // Auditor's footprint) — SDM sorts last, and every item under it stays
  // hidden by the permission gate regardless.
  // Keuangan (migration 0044: COA/journal/payroll/AP read) is AUDITOR's
  // other real read footprint alongside Inventory/Produksi — sorts right
  // after those, still ahead of Purchasing/Master Data/SDM.
  AUDITOR: ['Menu Utama', 'Inventory', 'Produksi', 'Keuangan', 'Aset & CRM', 'Purchasing', 'Master Data', 'SDM'],
  // FINANCE's real workspace as of FASE 10 (migration 0044) IS Keuangan —
  // COA/journal/payroll-processing/AP full create-approve-post — so it
  // sorts first after Dashboard, ahead of the PO-cost-exposure visibility
  // this role already had. FINANCE holds no FASE9 HR grant at all
  // (migration 0040 explicitly withheld it) — SDM still sorts last.
  FINANCE: ['Menu Utama', 'Keuangan', 'Purchasing', 'Produksi', 'Master Data', 'Inventory', 'SDM'],
  // HR's real workspace IS this group — SDM sorts first after Dashboard.
  // SYSTEM_ADMIN and PLATFORM_OWNER have no dedicated nav group in this
  // vertical slice (their real workspace is the dashboard itself — org
  // config for System Admin, cross-tenant overview for Platform Owner) and
  // hold no FASE9 HR grant either — Menu Utama first, default order after.
  // HR's migration 0044 grant is narrow (payroll_period.read,
  // payroll_fact.read/create only — no journal/COA/AP/payroll-run access,
  // per V5's explicit HR-vs-FINANCE split) — Keuangan is left unlisted
  // here and simply defaults to last, matching how minor a slice of that
  // group HR can actually see.
  HR: ['Menu Utama', 'SDM', 'Master Data', 'Purchasing', 'Inventory', 'Produksi'],
  // MANAGER holds leave_request/shift_swap.approve + attendance_event.create
  // (migration 0040) — real decision authority, not just visibility.
  MANAGER: ['Menu Utama', 'Master Data', 'Purchasing', 'SDM', 'Aset & CRM', 'Inventory', 'Produksi'],
  SYSTEM_ADMIN: ['Menu Utama', 'Master Data', 'Purchasing', 'Inventory'],
  PLATFORM_OWNER: ['Menu Utama', 'Master Data', 'Purchasing', 'Inventory'],
  // MAINTENANCE (FASE 11, migration 0048) — asset/maintenance operational
  // workflow IS this role's real workspace, sorts first after Dashboard.
  MAINTENANCE: ['Menu Utama', 'Aset & CRM', 'Master Data', 'Inventory'],
  // MARKETING (FASE 11, migration 0048) — customer/feedback/complaint/
  // consent workflow IS this role's real workspace.
  MARKETING: ['Menu Utama', 'Aset & CRM', 'Master Data'],
};

/**
 * Topbar identity must never show a long email as the primary visual
 * identity (owner directive). Real Supabase Auth `user_metadata.full_name`
 * (set at real account provisioning) is preferred when present; DEV/test
 * fixtures typically have none, so this derives a clean label from the
 * email's local-part instead of fabricating a name — e.g.
 * "budi.santoso@..." -> "Budi Santoso". The raw email is never hidden, only
 * demoted to the account dropdown.
 */
function deriveDisplayName(email: string | undefined, fullName: unknown): string {
  if (typeof fullName === 'string' && fullName.trim()) return fullName.trim();
  if (!email) return 'Pengguna';
  const localPart = email.split('@')[0] ?? '';
  const words = localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return words.length > 0 ? words.join(' ') : 'Pengguna';
}

function orderedNavGroups(role: RoleCode) {
  const priority = NAV_GROUP_PRIORITY[role];
  if (!priority) return NAV_GROUPS;
  return [...NAV_GROUPS].sort((a, b) => {
    const ai = priority.indexOf(a.label);
    const bi = priority.indexOf(b.label);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

const ROLE_PREVIEW_LABEL: Record<RoleCode, string> = {
  PLATFORM_OWNER: 'Platform Owner',
  TENANT_OWNER: 'Owner / Executive',
  MANAGER: 'Manager',
  SUPERVISOR: 'Supervisor',
  LEADER: 'Leader',
  FINANCE: 'Finance',
  PURCHASING: 'Purchasing',
  WAREHOUSE: 'Warehouse Staff',
  HR: 'HRD',
  AUDITOR: 'Auditor',
  SYSTEM_ADMIN: 'System Admin',
  MAINTENANCE: 'Maintenance',
  MARKETING: 'Marketing',
};
const PAGE_HELP: Record<string, string> = {
  '/app/dashboard': 'Ringkasan real-time modul yang bisa diakses role Anda.',
  '/app/items': 'Master data barang — dibaca & ditulis langsung dari database.',
  '/app/suppliers': 'Master data pemasok.',
  '/app/purchase-requests': 'Ajukan dan setujui permintaan pembelian (PR).',
  '/app/purchase-orders': 'Buat pesanan pembelian (PO) dari PR yang sudah disetujui.',
  '/app/goods-receipts': 'Terima barang dari PO, lalu posting ke Stock Ledger.',
  '/app/stock-ledger': 'Riwayat pergerakan stok — immutable, append-only.',
  '/app/stock-balance': 'Saldo stok per gudang, hasil proyeksi dari Stock Ledger.',
  '/app/stock-opname': 'Sesi hitung fisik — posting menulis selisih ke Stock Ledger.',
  '/app/transfers': 'Perpindahan stok antar gudang.',
  '/app/waste': 'Laporan barang rusak/kadaluarsa.',
  '/app/expiry': 'Pelacakan lot & tanggal kadaluarsa.',
  '/app/menus': 'Master menu — dasar untuk resep dan HPP.',
  '/app/recipes': 'Resep bahan baku per menu (BOM) dan HPP.',
  '/app/production': 'Batch produksi — konsumsi bahan baku dari resep.',
  '/app/employees': 'Roster karyawan — dasar untuk kehadiran, cuti, tukar shift, dan disiplin.',
  '/app/shifts': 'Definisi shift kerja (jam mulai/selesai, termasuk shift lintas hari) — dasar penjadwalan.',
  '/app/schedule': 'Penjadwalan karyawan per shift per tanggal — dasar kehadiran dan keterlambatan.',
  '/app/attendance': 'Riwayat clock-in/clock-out dan keterlambatan nyata.',
  '/app/leave': 'Pengajuan dan persetujuan cuti karyawan.',
  '/app/shift-swaps': 'Pengajuan dan persetujuan tukar jadwal kerja.',
  '/app/disciplinary': 'Tindakan disiplin karyawan — data sensitif, HR/Owner only.',
  '/app/finance/coa': 'Daftar akun akuntansi — dasar untuk semua posting jurnal.',
  '/app/finance/payroll': 'Periode, fakta gaji, dan payroll run — hanya fakta APPROVED yang pernah dibayarkan.',
  '/app/finance/ap': 'Invoice supplier dan pembayaran keluar — selalu telusur ke dokumen sumber nyata.',
  '/app/finance/journals': 'Jurnal double-entry dan periode akuntansi — koreksi selalu lewat reversal.',
  '/app/finance/reports': 'Trial Balance, Laba Rugi, dan Neraca draft — dihitung langsung dari jurnal terposting.',
  '/app/assets': 'Register aset — tag, cabang, kondisi, riwayat, dan pelepasan (maker-checker).',
  '/app/maintenance': 'Work order perawatan dan jadwal preventif — due/overdue nyata dari data, bukan rekaan.',
  '/app/crm': 'Identitas pelanggan, consent, feedback, dan komplain — loyalitas transaksional belum aktif (menyusul bersama POS).',
  '/app/command-center': 'KPI lintas modul dari metric_registry nyata — tidak ada angka penjualan/kas/POS (DEFERRED_WITH_POS).',
  '/app/reports': 'Generasi laporan harian/bulanan — snapshot immutable, tetap dapat direproduksi meski data sumber berubah.',
  '/app/notifications': 'Antrian notifikasi WhatsApp (SANDBOX) — outbox idempotent, percobaan terikat batas, kill switch outbound, rute penerima tersamar.',
  '/app/ai': 'Tanya AI, tinjau bukti tool, setujui/tolak usulan tindakan — dijawab oleh DEV_PROVIDER deterministik, tidak pernah menulis data ERP langsung.',
  '/app/migration': 'Dry-run migrasi data ke tenant clean-target terisolasi — mapping ambigu, rekonsiliasi, pengecualian, dan checkpoint rollback, tidak pernah menyentuh data operasional live.',
};

const PAGE_TITLE: Record<string, string> = {
  '/app/dashboard': 'Dashboard',
  '/app/items': 'Items',
  '/app/suppliers': 'Suppliers',
  '/app/purchase-requests': 'Purchase Requests',
  '/app/purchase-orders': 'Purchase Orders',
  '/app/goods-receipts': 'Goods Receipts',
  '/app/stock-ledger': 'Stock Ledger',
  '/app/stock-balance': 'Stock Balance',
  '/app/stock-opname': 'Stock Opname',
  '/app/transfers': 'Transfers',
  '/app/waste': 'Waste',
  '/app/expiry': 'Expiry / Lot',
  '/app/menus': 'Menu Master',
  '/app/recipes': 'Recipes / BOM',
  '/app/production': 'Produksi',
  '/app/employees': 'Karyawan',
  '/app/shifts': 'Shift Master',
  '/app/schedule': 'Jadwal Kerja',
  '/app/attendance': 'Kehadiran',
  '/app/leave': 'Cuti',
  '/app/shift-swaps': 'Tukar Shift',
  '/app/disciplinary': 'Disiplin',
  '/app/finance/coa': 'Chart of Accounts',
  '/app/finance/payroll': 'Payroll',
  '/app/finance/ap': 'Accounts Payable',
  '/app/finance/journals': 'Jurnal',
  '/app/finance/reports': 'Laporan Keuangan',
  '/app/assets': 'Aset',
  '/app/maintenance': 'Perawatan',
  '/app/crm': 'CRM',
  '/app/command-center': 'Command Center',
  '/app/reports': 'Report Center',
  '/app/notifications': 'Notifikasi',
  '/app/ai': 'AI Analyst',
  '/app/migration': 'Migration Rehearsal',
};

const ROLE_PREVIEW_ORDER: RoleCode[] = [
  'PLATFORM_OWNER',
  'TENANT_OWNER',
  'MANAGER',
  'SUPERVISOR',
  'LEADER',
  'FINANCE',
  'WAREHOUSE',
  'HR',
  'PURCHASING',
  'AUDITOR',
  'SYSTEM_ADMIN',
  'MAINTENANCE',
  'MARKETING',
];

/** Only these real (assigned) roles get the Dev/QA workspace-preview tool
 * — anyone who can plausibly need to sanity-check every role's layout.
 * Everyone else sees just their own real role, with no impersonation
 * control at all. This never changes hasPermission()/RLS/JWT — it only
 * controls whether AppShell renders the preview <select>; every mutation
 * everywhere in the app still reads real permissions from
 * PermissionContext, never from the previewed role. */
const WORKSPACE_PREVIEW_ELIGIBLE: RoleCode[] = ['TENANT_OWNER', 'PLATFORM_OWNER', 'SYSTEM_ADMIN'];

export default function AppShell() {
  const { session, signOut, loading: authLoading } = useAuth();
  const { context, hasPermission, loading: permLoading, error: permError, refresh: refreshPermissions } = usePermissions();
  const { branches, activeBranchId, setActiveBranchId, loading: branchLoading } = useBranch();
  const { previewRole, setPreviewRole, realRoles } = useDashboardPreview();
  const canUseWorkspacePreview = realRoles.some((r) => WORKSPACE_PREVIEW_ELIGIBLE.includes(r));
  // An override is only genuinely "active" when previewRole has actually
  // been steered away from the user's real primary role — not merely
  // because the Dev select exists. Drives the pill's neutral-vs-warning
  // styling below so it's never ambiguous whether a preview is changing
  // what's currently rendered.
  const previewOverrideActive = realRoles.length > 0 && previewRole !== realRoles[0];
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [activity, setActivity] = useState<ActivityEvent[] | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const bootstrappedOnce = useRef(false);

  const bootstrapping = authLoading || permLoading || branchLoading;
  if (!bootstrapping) bootstrappedOnce.current = true;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (bootstrapping && !bootstrappedOnce.current) {
    if (permError) {
      return <BootErrorScreen message={permError} onRetry={() => void refreshPermissions()} />;
    }
    return <BootScreen />;
  }

  const openNotifications = () => {
    setNotifOpen((v) => !v);
    setHelpOpen(false);
    if (activity === null) {
      listRecentActivity()
        .then(setActivity)
        .catch((e) => setActivityError(e instanceof Error ? e.message : String(e)));
    }
  };

  const goHome = () => {
    navigate('/app/dashboard');
    void refreshPermissions();
  };

  const roleSummary =
    context && context.authenticated
      ? context.roles.map((r) => ROLE_PREVIEW_LABEL[r.role_code] ?? r.role_code).join(', ') || '(no role assigned)'
      : '';
  const displayName = deriveDisplayName(session?.user?.email, session?.user?.user_metadata?.full_name);
  const sidebarWidth = collapsed ? 'var(--alex-sidebar-width-collapsed)' : 'var(--alex-sidebar-width)';
  const currentPageHelp = PAGE_HELP[location.pathname] ?? 'Halaman ini adalah bagian dari alur Vertical-Slice-1 ALEX ERP.';
  const currentPageTitle = PAGE_TITLE[location.pathname] ?? 'ALEX ERP';

  const sidebarContent = (
    <>
      <button
        onClick={goHome}
        title="ALEX ERP — kembali ke Dashboard"
        style={{ marginBottom: 18, paddingLeft: 7, minHeight: 38, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', display: 'block', width: '100%' }}
      >
        {!collapsed ? (
          <>
            <div style={{ fontWeight: 800, fontSize: 21, letterSpacing: -0.4 }}>
              <span style={{ color: 'var(--alex-sidebar-text-active)' }}>ALEX</span> <span style={{ color: 'var(--alex-primary)' }}>ERP</span>
            </div>
            <div style={{ fontWeight: 600, fontSize: 10.5, color: 'var(--alex-sidebar-text-muted)', marginTop: 2, letterSpacing: 0.3 }}>EL Amos Web ERP</div>
          </>
        ) : (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'var(--alex-primary)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            A
          </div>
        )}
      </button>

      <nav style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {orderedNavGroups(previewRole).map((group) => {
          const visible = group.items.filter((item) => !item.require || hasPermission(item.require.resource, item.require.action));
          if (visible.length === 0) return null;
          return (
            <div key={group.label}>
              {!collapsed && <GroupLabel>{group.label}</GroupLabel>}
              {visible.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={collapsed ? item.label : undefined}
                  style={({ isActive }) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: collapsed ? '9px' : '8.5px 11px 8.5px 10px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    margin: '1.5px 0',
                    borderRadius: 7,
                    fontSize: 'var(--alex-fs-sidebar-nav)',
                    fontWeight: isActive ? 600 : ('var(--alex-fw-sidebar-nav)' as unknown as number),
                    textDecoration: 'none',
                    color: isActive ? 'var(--alex-sidebar-text-active)' : 'var(--alex-sidebar-text)',
                    background: isActive ? 'var(--alex-sidebar-active)' : 'transparent',
                    borderLeft: isActive ? '2.5px solid var(--alex-primary)' : '2.5px solid transparent',
                    transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
                  })}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = 'var(--alex-sidebar-hover)';
                  }}
                  onMouseLeave={(e) => {
                    const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <item.icon size={16} />
                  {!collapsed && item.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Mobile-only: role/preview/branch controls live in the drawer instead of a topbar that has no room for them. */}
      <div className="alex-mobile-menu-btn" style={{ flexDirection: 'column', gap: 8, marginTop: 12, marginBottom: 12 }}>
        <div className="alex-topbar-pill" style={{ background: 'var(--alex-sidebar-hover)', border: 'none' }}>
          <span className="alex-topbar-pill-label" style={{ color: 'var(--alex-sidebar-text-muted)' }}>
            Peran Saat Ini
          </span>
          {realRoles.length > 1 ? (
            <select value={previewRole} onChange={(e) => setPreviewRole(e.target.value as RoleCode)} style={{ color: '#fff' }}>
              {realRoles.map((role) => (
                <option key={role} value={role}>
                  {ROLE_PREVIEW_LABEL[role]}
                </option>
              ))}
            </select>
          ) : (
            <span style={{ fontSize: 'var(--alex-fs-caption)', fontWeight: 600, color: '#fff' }}>{ROLE_PREVIEW_LABEL[realRoles[0] ?? previewRole]}</span>
          )}
        </div>
        {canUseWorkspacePreview && (
          <div
            className={`alex-topbar-dev-preview${previewOverrideActive ? ' alex-topbar-dev-preview-active' : ''}`}
            style={previewOverrideActive ? undefined : { borderColor: 'var(--alex-sidebar-hover)' }}
          >
            <span className="alex-topbar-dev-preview-label" style={previewOverrideActive ? undefined : { color: 'var(--alex-sidebar-text-muted)' }}>
              {previewOverrideActive ? 'Dev Preview · Aktif' : 'Dev Preview'}
            </span>
            <select value={previewRole} onChange={(e) => setPreviewRole(e.target.value as RoleCode)} style={previewOverrideActive ? undefined : { color: '#fff' }}>
              {ROLE_PREVIEW_ORDER.map((role) => (
                <option key={role} value={role}>
                  {ROLE_PREVIEW_LABEL[role]}
                </option>
              ))}
            </select>
          </div>
        )}
        {branches.length > 1 && (
          <div className="alex-topbar-pill" style={{ background: 'var(--alex-sidebar-hover)', border: 'none' }}>
            <span className="alex-topbar-pill-label" style={{ color: 'var(--alex-sidebar-text-muted)' }}>
              Cabang Aktif
            </span>
            <select value={activeBranchId ?? ''} onChange={(e) => setActiveBranchId(e.target.value)} style={{ color: '#fff' }}>
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <button
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? 'Perluas sidebar' : 'Ciutkan sidebar'}
        className="alex-sidebar-collapse-btn"
        style={{
          marginTop: 8,
          alignSelf: collapsed ? 'center' : 'flex-end',
          background: 'var(--alex-sidebar-hover)',
          border: 'none',
          color: 'var(--alex-sidebar-text)',
          borderRadius: 6,
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <IconMenu size={15} />
      </button>
      {!collapsed && <div style={{ fontSize: 9.5, color: 'var(--alex-sidebar-text-muted)', paddingLeft: 6, marginTop: 8 }}>© 2026 ALEX ERP</div>}
    </>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'var(--alex-font-family)', background: 'var(--alex-app-bg)' }}>
      <div className={`alex-sidebar-backdrop${mobileOpen ? ' alex-sidebar-open' : ''}`} onClick={() => setMobileOpen(false)} />
      <aside
        className={`alex-sidebar${mobileOpen ? ' alex-sidebar-open' : ''}`}
        style={{ width: sidebarWidth, background: 'var(--alex-sidebar-bg)', boxShadow: 'var(--alex-shadow-sidebar)', padding: '16px 10px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
      >
        {sidebarContent}
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            height: 'var(--alex-topbar-height)',
            padding: '0 var(--alex-content-padding)',
            background: 'var(--alex-topbar-bg)',
            borderBottom: '1px solid var(--alex-border)',
            fontSize: 'var(--alex-fs-body)',
            flexShrink: 0,
            gap: 14,
          }}
        >
          {/* LEFT — hamburger (mobile) / brand (mobile) / real "Peran Saat Ini" */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <button
              className="alex-mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              aria-label="Buka menu"
              style={{ width: 32, height: 32, alignItems: 'center', justifyContent: 'center', border: '1px solid var(--alex-border)', borderRadius: 'var(--alex-control-radius)', background: 'var(--alex-surface)', cursor: 'pointer', flexShrink: 0 }}
            >
              <IconMenu size={16} />
            </button>
            <button
              className="alex-topbar-mobile-brand"
              onClick={goHome}
              aria-label="ALEX ERP — kembali ke Dashboard"
              style={{ alignItems: 'center', gap: 6, border: 'none', background: 'none', cursor: 'pointer', padding: 0, minWidth: 0 }}
            >
              <div style={{ width: 25, height: 25, borderRadius: 7, background: 'var(--alex-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>A</div>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--alex-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>ALEX ERP</span>
            </button>
            <div className="alex-topbar-pills" style={{ gap: 8, minWidth: 0 }}>
              <div className="alex-topbar-pill alex-topbar-pill-role" title="Peran yang benar-benar ditetapkan server untuk akun Anda">
                <IconUsers size={14} />
                <div>
                  <span className="alex-topbar-pill-label">Peran Saat Ini</span>
                  {realRoles.length > 1 ? (
                    <select value={previewRole} onChange={(e) => setPreviewRole(e.target.value as RoleCode)}>
                      {realRoles.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_PREVIEW_LABEL[role]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)' }}>
                      {ROLE_PREVIEW_LABEL[realRoles[0] ?? previewRole]}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CENTER — Cabang Aktif context selector. This selector is always a branch
              picker (useBranch()) for every role including Platform Owner — there is
              no separate cross-tenant selector anywhere in the shell, so "Platform /
              Tenant Aktif" was inaccurate: Head Office Jakarta etc. are branches, not
              platforms or tenants. */}
          <div className="alex-topbar-pills" style={{ gap: 8, minWidth: 0, justifyContent: 'center' }}>
            <div className="alex-topbar-pill alex-topbar-pill-tenant" title="Cabang aktif untuk sesi ini">
              <IconBuilding size={14} />
              <div>
                <span className="alex-topbar-pill-label">Cabang Aktif</span>
                {branches.length > 1 ? (
                  <select value={activeBranchId ?? ''} onChange={(e) => setActiveBranchId(e.target.value)}>
                    {branches.map((b) => (
                      <option key={b.branch_id} value={b.branch_id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{ fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)' }}>{branches[0]?.name ?? '—'}</div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, justifyContent: 'flex-end' }}>
            {/* DEV workspace preview — deliberately small/secondary, never the dominant topbar element */}
            {canUseWorkspacePreview && (
              <div
                className={`alex-topbar-dev-preview${previewOverrideActive ? ' alex-topbar-dev-preview-active' : ''}`}
                title={
                  previewOverrideActive
                    ? `Preview aktif: menampilkan tata letak workspace ${ROLE_PREVIEW_LABEL[previewRole]} untuk QA — tidak mengubah hak akses nyata Anda, tidak melewati fn_check_permission()/RLS`
                    : 'Tidak ada preview aktif — dashboard menampilkan peran asli Anda. Pilih peran lain untuk pratinjau tata letak workspace-nya untuk QA saja.'
                }
              >
                <span className="alex-topbar-dev-preview-label">{previewOverrideActive ? 'Dev · Aktif' : 'Dev'}</span>
                <select value={previewRole} onChange={(e) => setPreviewRole(e.target.value as RoleCode)}>
                  {ROLE_PREVIEW_ORDER.map((role) => (
                    <option key={role} value={role}>
                      {ROLE_PREVIEW_LABEL[role]}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <IconGhostButton label="Cari (Ctrl/Cmd+K)" onClick={() => setPaletteOpen(true)}>
              <IconSearch size={16} />
            </IconGhostButton>
            <div style={{ position: 'relative' }}>
              <IconGhostButton label="Notifikasi" onClick={openNotifications}>
                <IconBell size={16} />
              </IconGhostButton>
              {notifOpen && (
                <div
                  className="alex-card"
                  style={{ position: 'absolute', right: 0, top: 38, width: 280, maxWidth: '85vw', padding: 12, zIndex: 20, boxShadow: 'var(--alex-shadow-popover)' }}
                >
                  <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)', marginBottom: 8, color: 'var(--alex-text-primary)' }}>Aktivitas terbaru</div>
                  {activityError && <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-danger)' }}>{activityError}</div>}
                  {!activityError && activity === null && <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-muted)' }}>Memuat...</div>}
                  {!activityError && activity !== null && activity.length === 0 && (
                    <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-muted)' }}>Belum ada aktivitas.</div>
                  )}
                  {activity && activity.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                      {activity.map((a) => (
                        <div key={a.event_id} style={{ fontSize: 'var(--alex-fs-micro)' }}>
                          <div style={{ color: 'var(--alex-text-primary)', fontWeight: 600 }}>{describeActivity(a)}</div>
                          <div style={{ color: 'var(--alex-text-faint)' }}>{new Date(a.occurred_at).toLocaleString('id-ID')}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <IconGhostButton
              label="Bantuan"
              onClick={() => {
                setHelpOpen((v) => !v);
                setNotifOpen(false);
              }}
            >
              <IconHelp size={16} />
            </IconGhostButton>

            <div style={{ position: 'relative', marginLeft: 4 }}>
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 'var(--alex-control-radius)' }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 27,
                    height: 27,
                    borderRadius: '50%',
                    background: 'var(--alex-primary-soft)',
                    color: 'var(--alex-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 12,
                    flexShrink: 0,
                  }}
                >
                  {displayName.slice(0, 1).toUpperCase()}
                </div>
                <div className="alex-topbar-brand-desktop" style={{ lineHeight: 1.25, textAlign: 'left' }}>
                  <div
                    style={{
                      color: 'var(--alex-text-primary)',
                      fontWeight: 700,
                      fontSize: 'var(--alex-fs-micro)',
                      maxWidth: 140,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {displayName}
                  </div>
                  {roleSummary && <div style={{ color: 'var(--alex-text-muted)', fontSize: 10 }}>{roleSummary}</div>}
                </div>
                <IconChevronDown size={13} className="alex-topbar-brand-desktop" />
              </button>
              {userMenuOpen && (
                <div className="alex-card" style={{ position: 'absolute', right: 0, top: 48, width: 220, padding: 8, zIndex: 20, boxShadow: 'var(--alex-shadow-popover)' }}>
                  <div style={{ padding: '6px 10px 10px', borderBottom: '1px solid var(--alex-border-soft)', marginBottom: 4 }}>
                    <div style={{ fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-primary)' }}>{displayName}</div>
                    <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-secondary)', wordBreak: 'break-all' }}>{session?.user?.email}</div>
                    {roleSummary && <div style={{ color: 'var(--alex-text-faint)', fontSize: 'var(--alex-fs-micro)' }}>{roleSummary}</div>}
                  </div>
                  <button
                    onClick={() => void signOut()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      background: 'none',
                      padding: '9px 10px',
                      borderRadius: 'var(--alex-control-radius)',
                      fontSize: 'var(--alex-fs-body)',
                      fontWeight: 600,
                      color: 'var(--alex-danger)',
                      cursor: 'pointer',
                    }}
                  >
                    <IconLogOut size={16} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main style={{ flex: 1, padding: 'var(--alex-content-padding)', overflow: 'auto' }}>
          <div className="alex-content">
            <Outlet />
          </div>
        </main>
      </div>
      {(notifOpen || userMenuOpen) && (
        <div
          onClick={() => {
            setNotifOpen(false);
            setUserMenuOpen(false);
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 15 }}
        />
      )}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <HelpCenter open={helpOpen} onClose={() => setHelpOpen(false)} currentPageTitle={currentPageTitle} currentPageHelp={currentPageHelp} />
    </div>
  );
}

function IconGhostButton({ children, label, onClick }: { children: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        width: 29,
        height: 29,
        borderRadius: '50%',
        border: '1px solid transparent',
        background: 'transparent',
        color: 'var(--alex-text-secondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--alex-surface-subtle)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {children}
    </button>
  );
}
